import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService, UserData } from './auth.service';
import { catchError, from, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';

function isInvalidDealerType(user: UserData): boolean {
  const dealerTypeId = environment.dealerTypeId;
  if (dealerTypeId === 1 && user.roles?.includes('ZimmaticUser')) return true;
  if (dealerTypeId === 2 && user.roles?.includes('ValleyUser')) return true;
  return false;
}

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return from(authService.isAuthenticated()).pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        return of(router.createUrlTree(['/login']));
      }

      const user$ = authService.currentUser
        ? of(authService.currentUser)
        : authService.getCurrentUser();

      return user$.pipe(
        switchMap((user) => {
          if (isInvalidDealerType(user)) {
            // Log out so the user doesn't loop between login and the guarded route
            return from(authService.logout()).pipe(
              map(() => router.createUrlTree(['/login'])),
            );
          }

          const allowedRoles = route.data['roles'] as string[] | undefined;

          if (!allowedRoles) {
            return of(true as boolean | ReturnType<Router['createUrlTree']>);
          }

          const hasRole =
            user.roles?.some((role) => allowedRoles.includes(role)) ?? false;
          return of(hasRole ? true : router.createUrlTree(['/login']));
        }),
        catchError(() => {
          return of(router.createUrlTree(['/login']));
        }),
      );
    }),
  );
};

export const loginRedirectGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return from(authService.isAuthenticated()).pipe(
    map((isAuthenticated) =>
      isAuthenticated ? router.createUrlTree(['/quoteRequest']) : true,
    ),
  );
};
